import { useCallback, useEffect, useMemo, useState } from "react";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import CancelButton from "@components/FlowActionButtons/CancelButton";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import { useNotification } from "@context/NotificationContext";
import type { VesselRegisterFormData } from "@features/vessel-register/schema/vesselRegisterFormSchema";
import {
  FORM_FIELDS,
  generateBunkerBargeAdditionalDocumentField,
  isBunkerBargeInProgress as getIsBunkerBargeInProgress,
} from "@features/vessel-register/utils";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { BunkerBargeDocumentType } from "@services/vessel/types";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import type { VesselOutletContext } from "../VesselRegistrationContainer";
import CancelBunkerBargeSideDrawer from "./Drawers/CancelBunkerBargeSideDrawer";
import { useUploadBunkerBargeDocuments } from "./useUploadBunkerBargeDocuments";

const LANG_BASE = "vessel-management.bunker-barge-page";
const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
const SINGLE_DOCUMENT_MAX_FILES = 1;
const ADDITIONAL_DOCUMENTS_MAX_FILES = 25;
const ADDITIONAL_DOCUMENT_NAME_MAX_CHARS = 50;

const BUNKER_BARGE_OFFSHORE_COMPANY_CODES_BY_NATURE_OF_SERVICE: Record<string, string> = {
  BUNKERING: "BNKR",
  DESLOP: "DSLP",
};

const getBunkerBargeCompaniesReferenceKey = (offshoreCompanyCode: string) =>
  `${REFERENCE_DATA_TYPES.COMPANIES_BY_OFFSHORE}?offshoreCompanyCode=${offshoreCompanyCode}`;

const BUNKER_BARGE_UPLOAD_CONFIG = {
  [FORM_FIELDS.NAVIGATION_LICENSE]: {
    certificateType: "NAVIGATION_LICENSE",
    maxFiles: SINGLE_DOCUMENT_MAX_FILES,
  },
  [FORM_FIELDS.CHARTER_PARTY_AGREEMENT]: {
    certificateType: "CHARTER_PARTY_AGREEMENT",
    maxFiles: SINGLE_DOCUMENT_MAX_FILES,
  },
  [FORM_FIELDS.OIL_SPILL_COMPANY]: {
    certificateType: "OIL_SPILL_COMPANY",
    maxFiles: SINGLE_DOCUMENT_MAX_FILES,
  },
  [FORM_FIELDS.SIRE_REPORT]: {
    certificateType: "SIRE_REPORT",
    maxFiles: SINGLE_DOCUMENT_MAX_FILES,
  },
  [FORM_FIELDS.HOSE_TEST_CERTIFICATE]: {
    certificateType: "HOSE_TEST_CERTIFICATE",
    maxFiles: SINGLE_DOCUMENT_MAX_FILES,
  },
  [FORM_FIELDS.SHIP_TO_SHIP_PLAN]: {
    certificateType: "SHIP_TO_SHIP_PLAN",
    maxFiles: SINGLE_DOCUMENT_MAX_FILES,
  },
  [FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS]: {
    certificateType: "ADDITIONAL_DOCUMENT",
    maxFiles: ADDITIONAL_DOCUMENTS_MAX_FILES,
  },
} as const;

type BunkerBargeUploadField = keyof typeof BUNKER_BARGE_UPLOAD_CONFIG;
type BunkerBargeUploadDocument = {
  name: string;
  url: string;
  type?: string;
  documentType?: BunkerBargeDocumentType | "";
  otherSpecifyType?: string;
  documentName?: string;
  documentSummary?: string;
  issueDate?: Date | string | null;
  expiryDate?: Date | string | null;
};

const BUNKER_BARGE_UPLOAD_FIELDS = Object.keys(
  BUNKER_BARGE_UPLOAD_CONFIG,
) as BunkerBargeUploadField[];

const BUNKER_BARGE_ADDITIONAL_DOCUMENT_TYPE_LABEL_KEYS: Record<BunkerBargeDocumentType, string> = {
  CERTIFICATE: "certificate",
  PERMIT: "permit",
  DOCUMENT: "document",
  INSURANCE: "insurance",
  VESSEL_MASTER_SOF: "vessel-master-sof",
  PRE_SAILING_MASTER_DECLARATION: "pre-sailing-master-declaration",
  OTHER_SPECIFY: "other-specify",
};
const BUNKER_BARGE_ADDITIONAL_DOCUMENT_TYPES: BunkerBargeDocumentType[] = [
  "CERTIFICATE",
  "PERMIT",
  "DOCUMENT",
  "INSURANCE",
  "VESSEL_MASTER_SOF",
  "PRE_SAILING_MASTER_DECLARATION",
  "OTHER_SPECIFY",
];

const isBunkerBargeUploadField = (field?: string): field is BunkerBargeUploadField =>
  BUNKER_BARGE_UPLOAD_FIELDS.includes(field as BunkerBargeUploadField);

const getDocumentEntriesFromValue = (value: unknown): BunkerBargeUploadDocument[] => {
  if (!Array.isArray(value)) return [];

  return value.filter((entry): entry is BunkerBargeUploadDocument => {
    if (!entry || entry instanceof File || typeof entry !== "object") return false;

    const candidate = entry as Record<string, unknown>;
    return typeof candidate.name === "string" && typeof candidate.url === "string";
  });
};

const mergeBunkerBargeDocumentMetadata = (
  documents: BunkerBargeUploadDocument[],
  metadataDocuments: BunkerBargeUploadDocument[],
) => {
  const maxLength = Math.max(documents.length, metadataDocuments.length);

  return Array.from({ length: maxLength }, (_value, idx) => {
    const document = documents[idx];
    const metadataDocument = metadataDocuments[idx];

    if (!document) return metadataDocument;
    if (!metadataDocument) return document;

    return {
      ...document,
      ...metadataDocument,
      name: document.name || metadataDocument.name,
      url: document.url || metadataDocument.url,
      type: document.type || metadataDocument.type,
    };
  }).filter((document): document is BunkerBargeUploadDocument => Boolean(document));
};

const buildDateMinValue = (dateValue: unknown) => {
  if (!dateValue) return undefined;

  const date = new Date(dateValue as string | Date);
  if (Number.isNaN(date.getTime())) return undefined;

  date.setDate(date.getDate() + 1);
  return date;
};

const getDocumentKey = (field: BunkerBargeUploadField, document: UploadEntry, idx: number) => {
  const entry = document as Record<string, unknown>;
  const url = typeof entry.url === "string" && entry.url.trim() ? entry.url : undefined;
  const name = typeof entry.name === "string" && entry.name.trim() ? entry.name : undefined;

  return url ?? name ?? `${field}-${idx}`;
};

const BunkerBarge = () => {
  const { t } = useTranslation();
  const {
    vesselDetails,
    formData,
    updateFormData,
    isRestrictedToCertificatesUpdate,
    isUpdateMode,
  } = useOutletContext<VesselOutletContext>();
  const { notify } = useNotification();

  const filesByKey = useUploadStore((state) => state.filesByKey);
  const uploadsInFlightByKey = useUploadStore((state) => state.uploadsInFlightByKey);
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const clearFilesForKey = useUploadStore((state) => state.clearFilesForKey);
  const isShippingAgent = useAuthStore((state) => state.isShippingAgentUser);
  const isBunkerBargeInProgress = getIsBunkerBargeInProgress(vesselDetails);

  const canCancelBunkerBarge = useMemo(
    () =>
      isShippingAgent && isUpdateMode && vesselDetails?.bunkerRegistered && isBunkerBargeInProgress,
    [isShippingAgent, isUpdateMode, vesselDetails?.bunkerRegistered, isBunkerBargeInProgress],
  );

  const { options: natureOfServices = [], isLoading: isNatureOfServicesLoading } =
    useFormattedReference<{
      code: string;
      description: string;
    }>(`${REFERENCE_DATA_TYPES.NATURE_OF_CALLS}?codes=BUNKERING&codes=DESLOP`, "bff-vessel");

  const natureOfServiceOptions = natureOfServices.map((natureOfCall) => ({
    label: String(natureOfCall.description),
    value: String(natureOfCall.code),
  }));

  const selectedNatureOfService = formData.watch(FORM_FIELDS.NATURE_OF_SERVICE);

  const selectedOffshoreCompanyCode = useMemo(
    () =>
      selectedNatureOfService
        ? BUNKER_BARGE_OFFSHORE_COMPANY_CODES_BY_NATURE_OF_SERVICE[selectedNatureOfService]
        : "",
    [selectedNatureOfService],
  );

  const fallbackOffshoreCompanyCode = useMemo(
    () => (selectedOffshoreCompanyCode === "BNKR" ? "DSLP" : "BNKR"),
    [selectedOffshoreCompanyCode],
  );

  const {
    options: selectedBunkeringCompanies = [],
    isLoading: isSelectedBunkeringCompaniesLoading,
  } = useFormattedReference<{
    companyCode: string;
    companyNameEnglish: string;
  }>(getBunkerBargeCompaniesReferenceKey(selectedOffshoreCompanyCode), "bff-vessel", {
    enabled: !isNatureOfServicesLoading && !!selectedOffshoreCompanyCode,
  });

  const { options: fallbackBunkeringCompanies = [] } = useFormattedReference<{
    companyCode: string;
    companyNameEnglish: string;
  }>(getBunkerBargeCompaniesReferenceKey(fallbackOffshoreCompanyCode), "bff-vessel", {
    enabled:
      !isNatureOfServicesLoading &&
      !isSelectedBunkeringCompaniesLoading &&
      selectedBunkeringCompanies.length === 0,
  });

  const bunkeringCompanies =
    selectedBunkeringCompanies.length > 0 ? selectedBunkeringCompanies : fallbackBunkeringCompanies;

  const bunkeringCompanyOptions = useMemo(
    () =>
      bunkeringCompanies.map((company) => ({
        label: String(company.companyNameEnglish),
        value: String(company.companyCode),
      })),
    [bunkeringCompanies],
  );

  const { options: oilSpillCompanies = [] } = useFormattedReference<{
    companyCode: string;
    companyNameEnglish: string;
  }>(`${REFERENCE_DATA_TYPES.COMPANIES_BY_OFFSHORE}?offshoreCompanyCode=OSR`, "bff-vessel");

  const oilSpillCompanyOptions = oilSpillCompanies.map((company) => ({
    label: String(company.companyNameEnglish),
    value: String(company.companyCode),
  }));

  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();

  const uploadConditions = {
    ...FILE_UPLOAD_PROFILES.certificates,
  };
  const bunkerBargeAdditionalDocumentTypeOptions = BUNKER_BARGE_ADDITIONAL_DOCUMENT_TYPES.map(
    (value) => ({
      label: t(
        `${LANG_BASE}.document-types.${BUNKER_BARGE_ADDITIONAL_DOCUMENT_TYPE_LABEL_KEYS[value]}`,
      ),
      value,
    }),
  );
  const [isCancelBunkerBargeDrawerOpen, setIsCancelBunkerBargeDrawerOpen] = useState(false);

  const setDocumentValue = useCallback(
    (field: BunkerBargeUploadField, documents: BunkerBargeUploadDocument[]) => {
      formData.setValue(field, documents, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      updateFormData(field, documents);
    },
    [formData, updateFormData],
  );

  const clearDocumentValue = useCallback(
    (field: BunkerBargeUploadField) => {
      setDocumentValue(field, []);
      clearFilesForKey(field);
    },
    [clearFilesForKey, setDocumentValue],
  );

  const getCurrentDocumentsForUpload = useCallback(
    (field: BunkerBargeUploadField) => {
      const storeDocuments = getDocumentEntriesFromValue(
        useUploadStore.getState().getFilesForKey(field),
      );

      if (field !== FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS) {
        return storeDocuments;
      }

      const formDocuments = getDocumentEntriesFromValue(formData.getValues(field));
      return mergeBunkerBargeDocumentMetadata(storeDocuments, formDocuments);
    },
    [formData],
  );

  const handleUploadSuccess = useCallback(
    ({
      documentUrls,
      startIdx,
      files,
      storeKey,
    }: {
      documentUrls: string[];
      startIdx: number;
      files: File[];
      storeKey?: string;
    }) => {
      if (!isBunkerBargeUploadField(storeKey)) return;
      const { maxFiles } = BUNKER_BARGE_UPLOAD_CONFIG[storeKey];
      const isAdditionalDocumentField = storeKey === FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS;

      const nextDocuments = [
        ...getDocumentEntriesFromValue(formData.getValues(storeKey)),
      ] as BunkerBargeUploadDocument[];

      documentUrls.forEach((url, fileOffset) => {
        const at = startIdx + fileOffset;
        const uploadedFile = files[fileOffset];
        const fileName = uploadedFile?.name ?? `document-${at + 1}`;
        const existingDocument = nextDocuments[at];
        nextDocuments[at] = {
          ...existingDocument,
          name: fileName,
          url,
          type: uploadedFile?.type,
          ...(isAdditionalDocumentField
            ? {
                documentType: existingDocument?.documentType ?? "",
                otherSpecifyType: existingDocument?.otherSpecifyType ?? "",
                documentName:
                  existingDocument?.documentName ??
                  fileName.slice(0, ADDITIONAL_DOCUMENT_NAME_MAX_CHARS),
                documentSummary: existingDocument?.documentSummary ?? "",
                issueDate: existingDocument?.issueDate ?? null,
                expiryDate: existingDocument?.expiryDate ?? null,
              }
            : {}),
        };
      });

      const uploadedDocuments = nextDocuments.filter((document) => !!document?.url);
      const normalizedDocuments =
        maxFiles > 0 ? uploadedDocuments.slice(-maxFiles) : uploadedDocuments;

      setDocumentValue(storeKey, normalizedDocuments);
      setFilesForKey(storeKey, normalizedDocuments, { mode: "replace", maxFiles });
      formData.clearErrors(storeKey);
    },
    [formData, setDocumentValue, setFilesForKey],
  );

  const handleUploadError = useCallback(
    (
      error: Error,
      meta: {
        startIdx: number;
        count: number;
        storeKey?: string;
      },
    ) => {
      notify({
        type: "error",
        title: t("vessel-management.notifications.certificate-upload-error-title"),
        description: t("vessel-management.notifications.certificate-upload-error-description"),
      });
      console.error("Error on upload bunker barge document:", error);

      if (
        isBunkerBargeUploadField(meta.storeKey) &&
        BUNKER_BARGE_UPLOAD_CONFIG[meta.storeKey].maxFiles === SINGLE_DOCUMENT_MAX_FILES
      ) {
        clearDocumentValue(meta.storeKey);
      } else if (isBunkerBargeUploadField(meta.storeKey)) {
        const toRemoveDescending = Array.from(
          { length: meta.count },
          (_value, idx) => meta.startIdx + idx,
        ).reverse();

        toRemoveDescending.forEach((idx) => {
          useUploadStore.getState().removeFileAtIndex(meta.storeKey!, idx);
        });
      }
    },
    [clearDocumentValue, notify, t],
  );

  const bunkerBargeDocumentMutation = useUploadBunkerBargeDocuments({
    handleUploadSuccess,
    handleUploadError,
  });

  useEffect(() => {
    const uploadStore = useUploadStore.getState();

    BUNKER_BARGE_UPLOAD_FIELDS.forEach((field) => {
      if (uploadStore.getFilesForKey(field).length) return;

      const storedDocuments = getDocumentEntriesFromValue(formData.getValues(field));
      if (storedDocuments.length) {
        uploadStore.setFilesForKey(field, storedDocuments, {
          mode: "replace",
          maxFiles: BUNKER_BARGE_UPLOAD_CONFIG[field].maxFiles,
        });
      }
    });
  }, [formData]);

  const effectiveStartDate = formData.watch(FORM_FIELDS.EFFECTIVE_START_DATE);
  const effectiveEndDate = formData.watch(FORM_FIELDS.EFFECTIVE_END_DATE);
  const oilSpillCompanyFromDate = formData.watch(FORM_FIELDS.OIL_SPILL_COMPANY_FROM_DATE);
  const navigationLicenseFromDate = formData.watch(FORM_FIELDS.NAVIGATION_LICENSE_FROM_DATE);
  const charterStartDate = formData.watch(FORM_FIELDS.CHARTER_START_DATE);
  const sireReportFromDate = formData.watch(FORM_FIELDS.SIRE_REPORT_FROM_DATE);
  const shipToShipPlanFromDate = formData.watch(FORM_FIELDS.SHIP_TO_SHIP_PLAN_FROM_DATE);
  const bunkerBargeAdditionalDocuments =
    formData.watch(FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS) ?? [];

  const getFilesForField = (field: BunkerBargeUploadField) =>
    filesByKey[field] ?? EMPTY_UPLOAD_ENTRIES;

  const isDocumentUploading = (field: BunkerBargeUploadField) =>
    (uploadsInFlightByKey[field] ?? 0) > 0;

  const handleReject = (field: BunkerBargeUploadField) => {
    const typesLabel = uploadConditions.format.join(", ");
    formData.setError(field, {
      type: "manual",
      message: t("form-validation.certificate-size-and-type", {
        max: uploadConditions.maxSizeLabel,
        types: typesLabel,
      }),
    });
  };

  const handleDocumentUpload = (field: BunkerBargeUploadField, files: File[]) => {
    if (!files?.length) return;
    const { certificateType, maxFiles } = BUNKER_BARGE_UPLOAD_CONFIG[field];
    const shouldReplaceExistingDocuments = maxFiles === SINGLE_DOCUMENT_MAX_FILES;
    const currentDocuments = getCurrentDocumentsForUpload(field);

    if (shouldReplaceExistingDocuments) {
      setDocumentValue(field, []);
    } else {
      setDocumentValue(field, currentDocuments);
    }
    bunkerBargeDocumentMutation.mutate({
      files,
      startIdx: shouldReplaceExistingDocuments ? 0 : currentDocuments.length,
      certificateType,
      storeKey: field,
    });
  };

  const setDocumentFiles = (field: BunkerBargeUploadField, files: UploadEntry[]) => {
    if (field === FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS) {
      const currentDocuments = getCurrentDocumentsForUpload(field);
      const incomingUploadedDocuments = getDocumentEntriesFromValue(files);
      const incomingFiles = files.filter((file): file is File => file instanceof File);
      const mergedDocuments = mergeBunkerBargeDocumentMetadata(
        incomingUploadedDocuments,
        currentDocuments,
      );

      setFilesForKey(field, [...mergedDocuments, ...incomingFiles], {
        mode: "replace",
        maxFiles: BUNKER_BARGE_UPLOAD_CONFIG[field].maxFiles,
      });
      return;
    }

    setFilesForKey(field, files, {
      mode: "replace",
      maxFiles: BUNKER_BARGE_UPLOAD_CONFIG[field].maxFiles,
    });
  };

  const handleRemoveDocument = (field: BunkerBargeUploadField, index: number) => {
    const currentDocuments = getDocumentEntriesFromValue(formData.getValues(field));
    const nextDocuments = currentDocuments.filter((_document, idx) => idx !== index);

    setDocumentValue(field, nextDocuments);
  };

  const validateUploadFiles = useCallback(
    (storeKey: BunkerBargeUploadField, incomingFiles: File[], maxFiles: number) => {
      const existingFilesCount = useUploadStore.getState().getFilesForKey(storeKey).length ?? 0;

      if (existingFilesCount + incomingFiles.length > maxFiles) {
        return t("form-validation.max-images", { max: maxFiles });
      }

      return null;
    },
    [t],
  );

  const disableEffectiveDates = useMemo(
    () => isRestrictedToCertificatesUpdate || (isUpdateMode && vesselDetails?.bunkerRegistered),
    [isRestrictedToCertificatesUpdate, isUpdateMode, vesselDetails?.bunkerRegistered],
  );

  const renderAdditionalDocumentMetadata = (idx: number) => {
    const document = bunkerBargeAdditionalDocuments[idx] as BunkerBargeUploadDocument | undefined;
    const documentType = document?.documentType;
    const issueDate = document?.issueDate;

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FormItem<VesselRegisterFormData>
          testId={`bunker-barge-additional-documents-${idx}-document-type`}
          name={generateBunkerBargeAdditionalDocumentField.documentType(idx)}
          type="select"
          options={bunkerBargeAdditionalDocumentTypeOptions}
          label={t(`${LANG_BASE}.document-type`)}
          onFieldChange={(key, value) => {
            updateFormData(key, value);

            if (value !== "CERTIFICATE") {
              formData.clearErrors(generateBunkerBargeAdditionalDocumentField.expiryDate(idx));
            }
          }}
          disabled={isRestrictedToCertificatesUpdate}
        />
        {documentType === "OTHER_SPECIFY" && (
          <FormItem<VesselRegisterFormData>
            testId={`bunker-barge-additional-documents-${idx}-other-specify-type`}
            name={generateBunkerBargeAdditionalDocumentField.otherSpecifyType(idx)}
            type="text"
            maxChars={30}
            label={t(`${LANG_BASE}.other-specify-type`)}
            onFieldChange={updateFormData}
            disabled={isRestrictedToCertificatesUpdate}
          />
        )}
        {documentType !== "OTHER_SPECIFY" && <div className="hidden lg:block" aria-hidden="true" />}
        <div className="lg:col-span-2">
          <FormItem<VesselRegisterFormData>
            testId={`bunker-barge-additional-documents-${idx}-document-name`}
            name={generateBunkerBargeAdditionalDocumentField.documentName(idx)}
            type="text"
            maxChars={ADDITIONAL_DOCUMENT_NAME_MAX_CHARS}
            label={t("vessel-management.document-name")}
            onFieldChange={updateFormData}
            required
            disabled={isRestrictedToCertificatesUpdate}
          />
        </div>
        <FormItem<VesselRegisterFormData>
          testId={`bunker-barge-additional-documents-${idx}-issue-date`}
          name={generateBunkerBargeAdditionalDocumentField.issueDate(idx)}
          type="date-picker"
          label={t(`${LANG_BASE}.issue-date`)}
          onFieldChange={(k, v) => {
            updateFormData(k, v);
            formData.trigger([
              generateBunkerBargeAdditionalDocumentField.issueDate(idx),
              generateBunkerBargeAdditionalDocumentField.expiryDate(idx),
            ]);
          }}
          timePicker={false}
          disabled={isRestrictedToCertificatesUpdate}
        />
        <FormItem<VesselRegisterFormData>
          testId={`bunker-barge-additional-documents-${idx}-expiry-date`}
          name={generateBunkerBargeAdditionalDocumentField.expiryDate(idx)}
          type="date-picker"
          label={t(`${LANG_BASE}.expiry-date`)}
          onFieldChange={(k, v) => {
            updateFormData(k, v);
            formData.trigger([
              generateBunkerBargeAdditionalDocumentField.issueDate(idx),
              generateBunkerBargeAdditionalDocumentField.expiryDate(idx),
            ]);
          }}
          timePicker={false}
          dateConstraint="not-past"
          minDate={buildDateMinValue(issueDate)}
          required={documentType === "CERTIFICATE"}
          disabled={isRestrictedToCertificatesUpdate}
        />
        <div className="lg:col-span-2">
          <FormItem<VesselRegisterFormData>
            testId={`bunker-barge-additional-documents-${idx}-document-summary`}
            name={generateBunkerBargeAdditionalDocumentField.documentSummary(idx)}
            type="text"
            maxChars={300}
            label={t("vessel-management.document-summary")}
            onFieldChange={updateFormData}
            disabled={isRestrictedToCertificatesUpdate}
          />
        </div>
      </div>
    );
  };

  const renderDocumentUpload = (field: BunkerBargeUploadField, testId: string) => {
    const files = getFilesForField(field);
    const isUploading = isDocumentUploading(field);
    const { maxFiles } = BUNKER_BARGE_UPLOAD_CONFIG[field];
    const hasFiles = files.length > 0;
    const isSingleUpload = maxFiles === SINGLE_DOCUMENT_MAX_FILES;
    const hasReachedMaxFiles = isSingleUpload ? hasFiles : files.length >= maxFiles;

    return (
      <div className="col-span-full flex flex-col">
        <FormItem<VesselRegisterFormData>
          name={field}
          type="file"
          uploadConditions={uploadConditions}
          testId={testId}
          files={files}
          setFiles={(nextFiles) => setDocumentFiles(field, nextFiles)}
          onUpload={(_key, uploadedFiles) => handleDocumentUpload(field, uploadedFiles)}
          onReject={() => handleReject(field)}
          onFieldChange={updateFormData}
          disabled={
            (isSingleUpload ? false : isRestrictedToCertificatesUpdate) ||
            isUploading ||
            hasReachedMaxFiles
          }
          maxFiles={maxFiles}
          notSuccess={isUploading}
          validateDrop={(selectedCount) => {
            if (selectedCount > maxFiles) {
              return t("form-validation.max-images", { max: maxFiles });
            }
            return null;
          }}
          fileValidation={(incomingFiles) => validateUploadFiles(field, incomingFiles, maxFiles)}
        />
        {files.map((document, idx) => {
          const documentKey = getDocumentKey(field, document, idx);
          const isAdditionalDocumentField = field === FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS;
          const hasUploadedUrl = Boolean((document as Record<string, unknown>).url);

          return (
            <div className="grid grid-cols-1 gap-4" key={documentKey}>
              <SingleFile
                testId={`document-${field}-${idx}`}
                file={document}
                fieldName={field}
                currentFileIndex={idx}
                setFiles={() => handleRemoveDocument(field, idx)}
                removeFn={() => formData.clearErrors(field)}
                isLoading={isUploading && document instanceof File}
                disabled={isSingleUpload ? false : isRestrictedToCertificatesUpdate}
              />
              {isAdditionalDocumentField && hasUploadedUrl && renderAdditionalDocumentMetadata(idx)}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div data-testid="vessels-type-page" className="pb-8">
      <FormProvider {...formData}>
        <div className="flex flex-col gap-0">
          {canCancelBunkerBarge && (
            <div className="flex justify-end mb-4">
              <CancelButton
                langBase={LANG_BASE}
                // TODO: I don't a see permission for this action, check if we need it
                permission=""
                isActionAvailable={canCancelBunkerBarge}
                onCancel={() => {
                  setIsCancelBunkerBargeDrawerOpen(true);
                }}
              />
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.NATURE_OF_SERVICE>
              testId="register-nature-of-service-autocomplete"
              name={FORM_FIELDS.NATURE_OF_SERVICE}
              label={t(`${LANG_BASE}.nature-of-service`)}
              type="autocomplete"
              options={natureOfServiceOptions}
              onFieldChange={updateFormData}
              disabled={isRestrictedToCertificatesUpdate}
            />
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.BUNKERING_COMPANY>
              testId="register-company-name-autocomplete"
              name={FORM_FIELDS.BUNKERING_COMPANY}
              label={t(`${LANG_BASE}.company-name`)}
              type="autocomplete"
              options={bunkeringCompanyOptions}
              onFieldChange={updateFormData}
              disabled={isRestrictedToCertificatesUpdate}
            />
          </div>
        </div>

        <div className="flex flex-col gap-8">
          <FormSection title={t(`${LANG_BASE}.form-sections.navigation-license`)}>
            {renderDocumentUpload(FORM_FIELDS.NAVIGATION_LICENSE, "navigation-license-file")}
            <div className="col-span-full grid grid-cols-1 lg:grid-cols-2 gap-4">
              <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.NAVIGATION_LICENSE_FROM_DATE>
                name={FORM_FIELDS.NAVIGATION_LICENSE_FROM_DATE}
                type="date-picker"
                label={t(`${LANG_BASE}.issue-date`)}
                testId="navigation-license-issue-date"
                onFieldChange={(k, v) => {
                  updateFormData(k, v);
                  formData.trigger([
                    FORM_FIELDS.NAVIGATION_LICENSE_FROM_DATE,
                    FORM_FIELDS.NAVIGATION_LICENSE_TO_DATE,
                  ]);
                }}
                timePicker={false}
              />
              <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.NAVIGATION_LICENSE_TO_DATE>
                name={FORM_FIELDS.NAVIGATION_LICENSE_TO_DATE}
                type="date-picker"
                label={t(`${LANG_BASE}.expiry-date`)}
                testId="navigation-license-expiry-date"
                onFieldChange={(k, v) => {
                  updateFormData(k, v);
                  formData.trigger([
                    FORM_FIELDS.NAVIGATION_LICENSE_FROM_DATE,
                    FORM_FIELDS.NAVIGATION_LICENSE_TO_DATE,
                  ]);
                }}
                timePicker={false}
                dateConstraint="not-past"
                minDate={buildDateMinValue(navigationLicenseFromDate)}
              />
            </div>
          </FormSection>
          <FormSection title={t(`${LANG_BASE}.form-sections.charter-party-agreement`)}>
            {renderDocumentUpload(
              FORM_FIELDS.CHARTER_PARTY_AGREEMENT,
              "charter-party-agreement-file",
            )}
            <div className="col-span-full">
              <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.REGISTERED_OWNER>
                testId="register-owner-input"
                name={FORM_FIELDS.REGISTERED_OWNER}
                type="text"
                maxChars={150}
                label={t(`${LANG_BASE}.register-owner`)}
                onFieldChange={updateFormData}
                disabled={isRestrictedToCertificatesUpdate}
              />
            </div>
            <div className="col-span-full grid grid-cols-1 lg:grid-cols-2 gap-4">
              <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.CHARTER_START_DATE>
                name={FORM_FIELDS.CHARTER_START_DATE}
                type="date-picker"
                label={t(`${LANG_BASE}.from-date`)}
                testId="charter-from-date"
                onFieldChange={(k, v) => {
                  updateFormData(k, v);
                  formData.trigger([FORM_FIELDS.CHARTER_START_DATE, FORM_FIELDS.CHARTER_END_DATE]);
                }}
                timePicker={false}
              />
              <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.CHARTER_END_DATE>
                name={FORM_FIELDS.CHARTER_END_DATE}
                type="date-picker"
                label={t(`${LANG_BASE}.to-date`)}
                testId="charter-to-date"
                onFieldChange={(k, v) => {
                  updateFormData(k, v);
                  formData.trigger([FORM_FIELDS.CHARTER_START_DATE, FORM_FIELDS.CHARTER_END_DATE]);
                }}
                timePicker={false}
                dateConstraint="not-past"
                minDate={buildDateMinValue(charterStartDate)}
              />
            </div>
          </FormSection>
          <FormSection title={t(`${LANG_BASE}.form-sections.oil-spill-company-details`)}>
            {renderDocumentUpload(FORM_FIELDS.OIL_SPILL_COMPANY, "oil-spill-company-file")}
            <div className="col-span-full grid grid-cols-1 lg:grid-cols-2 gap-4">
              <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.OIL_COMPANY>
                testId="company-name-input"
                name={FORM_FIELDS.OIL_COMPANY}
                type="autocomplete"
                options={oilSpillCompanyOptions}
                label={t(`${LANG_BASE}.company-name`)}
                onFieldChange={updateFormData}
                disabled={isRestrictedToCertificatesUpdate}
              />
              <div className="hidden lg:block" aria-hidden="true" />
              <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.OIL_SPILL_COMPANY_FROM_DATE>
                name={FORM_FIELDS.OIL_SPILL_COMPANY_FROM_DATE}
                type="date-picker"
                label={t(`${LANG_BASE}.from-date`)}
                testId="oil-spill-company-from-date"
                onFieldChange={(k, v) => {
                  updateFormData(k, v);
                  formData.trigger([
                    FORM_FIELDS.OIL_SPILL_COMPANY_FROM_DATE,
                    FORM_FIELDS.OIL_SPILL_COMPANY_TO_DATE,
                  ]);
                }}
                timePicker={false}
              />
              <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.OIL_SPILL_COMPANY_TO_DATE>
                name={FORM_FIELDS.OIL_SPILL_COMPANY_TO_DATE}
                type="date-picker"
                label={t(`${LANG_BASE}.to-date`)}
                testId="oil-spill-company-to-date"
                onFieldChange={(k, v) => {
                  updateFormData(k, v);
                  formData.trigger([
                    FORM_FIELDS.OIL_SPILL_COMPANY_FROM_DATE,
                    FORM_FIELDS.OIL_SPILL_COMPANY_TO_DATE,
                  ]);
                }}
                timePicker={false}
                dateConstraint="not-past"
                minDate={buildDateMinValue(oilSpillCompanyFromDate)}
              />
            </div>
          </FormSection>
          <FormSection title={t(`${LANG_BASE}.form-sections.sire-report`)}>
            {renderDocumentUpload(FORM_FIELDS.SIRE_REPORT, "sire-report-file")}
            <div className="col-span-full grid grid-cols-1 lg:grid-cols-2 gap-4">
              <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.SIRE_REPORT_FROM_DATE>
                name={FORM_FIELDS.SIRE_REPORT_FROM_DATE}
                type="date-picker"
                label={t(`${LANG_BASE}.from-date`)}
                testId="sire-report-from-date"
                onFieldChange={(k, v) => {
                  updateFormData(k, v);
                  formData.trigger([
                    FORM_FIELDS.SIRE_REPORT_FROM_DATE,
                    FORM_FIELDS.SIRE_REPORT_TO_DATE,
                  ]);
                }}
                timePicker={false}
              />
              <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.SIRE_REPORT_TO_DATE>
                name={FORM_FIELDS.SIRE_REPORT_TO_DATE}
                type="date-picker"
                label={t(`${LANG_BASE}.to-date`)}
                testId="sire-report-to-date"
                onFieldChange={(k, v) => {
                  updateFormData(k, v);
                  formData.trigger([
                    FORM_FIELDS.SIRE_REPORT_FROM_DATE,
                    FORM_FIELDS.SIRE_REPORT_TO_DATE,
                  ]);
                }}
                timePicker={false}
                dateConstraint="not-past"
                minDate={buildDateMinValue(sireReportFromDate)}
              />
            </div>
          </FormSection>
          <FormSection title={t(`${LANG_BASE}.form-sections.hose-test-certificate`)}>
            {renderDocumentUpload(FORM_FIELDS.HOSE_TEST_CERTIFICATE, "hose-test-certificate-file")}
            <div className="col-span-full grid grid-cols-1 lg:grid-cols-2 gap-4">
              <FormItem<
                VesselRegisterFormData,
                typeof FORM_FIELDS.COMPANY_NAME_HOSE_TEST_CERTIFICATE
              >
                testId="hose-test-certificate-company-name"
                name={FORM_FIELDS.COMPANY_NAME_HOSE_TEST_CERTIFICATE}
                type="text"
                maxChars={50}
                label={t(`${LANG_BASE}.company-name`)}
                onFieldChange={updateFormData}
                disabled={isRestrictedToCertificatesUpdate}
              />
              <FormItem<
                VesselRegisterFormData,
                typeof FORM_FIELDS.HOSE_TEST_CERTIFICATE_EXPIRY_DATE
              >
                name={FORM_FIELDS.HOSE_TEST_CERTIFICATE_EXPIRY_DATE}
                type="date-picker"
                label={t(`${LANG_BASE}.expiry-date`)}
                testId="hose-test-certificate-expiry-date"
                onFieldChange={updateFormData}
                timePicker={false}
                dateConstraint="not-past"
              />
            </div>
          </FormSection>
          <FormSection title={t(`${LANG_BASE}.form-sections.ship-to-ship-plan`)}>
            {renderDocumentUpload(FORM_FIELDS.SHIP_TO_SHIP_PLAN, "ship-to-ship-plan-file")}
            <div className="col-span-full grid grid-cols-1 lg:grid-cols-2 gap-4">
              <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.SHIP_TO_SHIP_PLAN_FROM_DATE>
                name={FORM_FIELDS.SHIP_TO_SHIP_PLAN_FROM_DATE}
                type="date-picker"
                label={t(`${LANG_BASE}.from-date`)}
                testId="ship-to-ship-plan-from-date"
                onFieldChange={(k, v) => {
                  updateFormData(k, v);
                  formData.trigger([
                    FORM_FIELDS.SHIP_TO_SHIP_PLAN_FROM_DATE,
                    FORM_FIELDS.SHIP_TO_SHIP_PLAN_TO_DATE,
                  ]);
                }}
                timePicker={false}
              />
              <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.SHIP_TO_SHIP_PLAN_TO_DATE>
                name={FORM_FIELDS.SHIP_TO_SHIP_PLAN_TO_DATE}
                type="date-picker"
                label={t(`${LANG_BASE}.to-date`)}
                testId="ship-to-ship-plan-to-date"
                onFieldChange={(k, v) => {
                  updateFormData(k, v);
                  formData.trigger([
                    FORM_FIELDS.SHIP_TO_SHIP_PLAN_FROM_DATE,
                    FORM_FIELDS.SHIP_TO_SHIP_PLAN_TO_DATE,
                  ]);
                }}
                timePicker={false}
                dateConstraint="not-past"
                minDate={buildDateMinValue(shipToShipPlanFromDate)}
              />
            </div>
          </FormSection>
          <FormSection title={t(`${LANG_BASE}.form-sections.effective-dates`)}>
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.EFFECTIVE_START_DATE>
              name={FORM_FIELDS.EFFECTIVE_START_DATE}
              type="date-picker"
              label={t(`${LANG_BASE}.start-date`)}
              testId="effective-start-date"
              onFieldChange={(k, v) => {
                updateFormData(k, v);
                formData.trigger([
                  FORM_FIELDS.EFFECTIVE_START_DATE,
                  FORM_FIELDS.EFFECTIVE_END_DATE,
                ]);
              }}
              timePicker={false}
              dateConstraint="not-past"
              disabled={disableEffectiveDates}
            />
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.EFFECTIVE_END_DATE>
              name={FORM_FIELDS.EFFECTIVE_END_DATE}
              type="date-picker"
              label={t(`${LANG_BASE}.end-date`)}
              testId="effective-end-date"
              onFieldChange={(k, v) => {
                updateFormData(k, v);
                formData.trigger([
                  FORM_FIELDS.EFFECTIVE_START_DATE,
                  FORM_FIELDS.EFFECTIVE_END_DATE,
                ]);
              }}
              timePicker={false}
              dateConstraint="not-past"
              minDate={buildDateMinValue(effectiveStartDate)}
              disabled={disableEffectiveDates}
            />
            <div className="md:col-span-2 md:col-start-1">
              <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.REMARKS>
                testId="remarks-input"
                name={FORM_FIELDS.REMARKS}
                type="text"
                maxChars={1500}
                label={t(`${LANG_BASE}.remarks`)}
                onFieldChange={updateFormData}
                disabled={isRestrictedToCertificatesUpdate}
              />
            </div>
          </FormSection>
          <FormSection title={t(`${LANG_BASE}.form-sections.additional-documents`)}>
            {renderDocumentUpload(
              FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS,
              "bunker-barge-additional-documents-file",
            )}
          </FormSection>
        </div>
      </FormProvider>
      <CancelBunkerBargeSideDrawer
        vesselId={vesselDetails?.id || ""}
        version={vesselDetails?.bunkerBargeDetails?.version ?? 0}
        isOpen={isCancelBunkerBargeDrawerOpen}
        onClose={() => setIsCancelBunkerBargeDrawerOpen(false)}
        onSuccess={() => {
          setIsCancelBunkerBargeDrawerOpen(false);
        }}
        endDate={effectiveEndDate}
      />
    </div>
  );
};

export default BunkerBarge;
